/**
 * Os ícones do menu — uma família só.
 *
 * 20×20, traço 1,7, pontas e junções redondas, e o mesmo peso óptico. O
 * conjunto anterior misturava espessuras, então alguns ícones pesavam mais que
 * os outros sem querer dizer nada com isso.
 *
 * Comparação antes/depois: `ui_kits/menu/index.html`.
 * Por que cada um mudou: `MENU.md`.
 */

import type { ReactNode } from 'react';

export type NomeDoIcone =
  | 'sol'
  | 'lua'
  | 'atendimento'
  | 'fila'
  | 'funil'
  | 'clientes'
  | 'tarefas'
  | 'painel'
  | 'identidade'
  | 'configuracoes'
  | 'inicio'
  | 'recolher'
  | 'sair'
  /*
   * Os dois da LINHA DO TEMPO que o menu não tinha.
   *
   * Ela usava `💬 ↗ ✓ ✎ ⇩`. Emoji renderiza diferente em cada sistema, não
   * herda `currentColor` — então não acompanha o tema escuro nem a cor do
   * disco — e o peso óptico varia de fonte para fonte, o que quebra justamente
   * a família que este arquivo existe para manter.
   *
   * Mensagem, mudança de etapa e tarefa reaproveitam `atendimento`, `funil` e
   * `tarefas`: são os mesmos assuntos, e um segundo desenho para a mesma coisa
   * ensinaria que são coisas diferentes.
   */
  | 'nota'
  | 'portal'
  /* Criar algo novo: o mais dentro do círculo (Nova organização). */
  | 'criar'
  /* Trocar: duas setas em sentidos opostos (Trocar de organização). */
  | 'trocar'
  /* A planilha: o "Exportar" dos relatórios, que abre no Excel. */
  | 'planilha'
  /* O ✓ da opção escolhida numa lista (`CampoEscolha`). */
  | 'marcado'
  /* A bandeira: a prioridade da conversa, na ficha do painel. */
  | 'prioridade'
  /*
   * O sino da notificação.
   *
   * Desenhado aqui como os outros, e não importado: mesma família de traço,
   * mesma caixa de 20px, e ele precisa herdar `currentColor` porque muda de cor
   * quando o número aparece dentro dele.
   */
  | 'sino'
  /*
   * O voltar, que vivia como SVG solto dentro de `cab-linha.tsx`.
   *
   * Mesmo desenho, mesmo traço — e fora da família, ele era o único ícone do
   * produto que ninguém encontrava procurando aqui. O Cliente 360 precisou de
   * um voltar e nasceu com uma seta de TEXTO, "←", porque o conjunto não
   * oferecia nenhuma: glifo de fonte não tem o mesmo peso óptico nem a mesma
   * caixa dos outros, e é exatamente o que este arquivo existe para evitar.
   */
  | 'voltar'
  /*
   * O CHEVRON que abre. Mesmo desenho do `voltar`, espelhado — porque é a
   * mesma seta, e desenhá-la de novo produziria duas setas com pesos ópticos
   * diferentes na mesma tela.
   *
   * Ele gira 90° ao abrir o cartão de tarefa, e é o único sinal de "isto
   * abre": o triângulo nativo do `<details>` fica ANTES do texto e empurraria
   * o assunto da tarefa para o segundo lugar da leitura.
   */
  | 'direita'
  /*
   * A lixeira. Tampa, corpo e duas ranhuras — sem as ranhuras ela lê como uma
   * sacola, que é o gesto oposto.
   */
  | 'lixeira'
  /*
   * Os quatro dos documentos da oportunidade (25/09/2026): enviar, abrir para
   * ver, e os dois que fazem as vezes de prévia quando o arquivo não é foto
   * nem vídeo. Desenhados aqui pelo mesmo motivo do sino: mesma família de
   * traço, e herdam `currentColor` — o PDF fica vermelho pela cor, não por um
   * segundo desenho.
   */
  | 'enviar'
  | 'olho'
  | 'documento'
  | 'arquivo'
  /*
   * O brilho da IA, e os três da tela que a divulga (28/09/2026): analisar,
   * anotar e o próximo passo. Moram aqui pela razão do cabeçalho — um SVG solto
   * na tela seria o ícone que ninguém acha procurando neste arquivo.
   */
  | 'ia'
  | 'lupa'
  | 'lapis'
  | 'seta'
  /*
   * O raio das Automações (29/09/2026), no menu e na tela que as divulga: o
   * sinal que o mercado já ensinou como "acontece sozinho".
   */
  | 'automacao'
  /*
   * Os cinco do card do funil (28/09/2026): o contato, o tempo na etapa, a
   * tarefa nova, o passar para outra pessoa e o chevron do "Mover". Redesenhados
   * na caixa de 20px a partir da referência de 24px, para ficarem na família.
   */
  | 'pessoa'
  | 'relogio'
  | 'tarefa-nova'
  | 'passar'
  | 'abaixo'
  /*
   * Os seis canais da tela de Conexões (29/09/2026). Não são os logos
   * oficiais: são desenhos de traço na caixa de 20px que LEMBRAM cada marca,
   * para ficarem na família. A cor da marca vem de fora, por `currentColor`.
   */
  | 'whatsapp'
  | 'instagram'
  | 'facebook'
  | 'web-chat'
  | 'api'
  | 'tiktok'
  /* O sino com o som desligado, e a menção da nota interna (#569). */
  | 'sino-mudo'
  | 'arroba'
  /*
   * Os dois da busca da lista de conversas (30/09/2026). `buscar` é a lupa
   * SEM o "+": a `lupa` quer dizer examinar de perto, e o campo de busca com
   * ela ensinaria que ele amplia alguma coisa. `fechar` é o × do chip de filtro
   * aplicado, que antes seria um glifo de fonte fora da família.
   */
  | 'buscar'
  | 'fechar'
  /* As linhas da ficha do cliente no painel (v3, #677): telefone, e-mail,
     etiqueta, caixa e o copiar do telefone. */
  | 'telefone'
  | 'email'
  | 'etiqueta'
  | 'predio'
  | 'caixa'
  | 'copiar'
  /* As automações (fase 2): a situação de cada uma, os números e o modelo de
     onde ela saiu. Desenhos do protótipo das automações, no mesmo traço. */
  | 'check'
  | 'alerta'
  | 'pausa'
  | 'repetir'
  | 'frasco'
  | 'menu'
  | 'calendario'
  /* O "+" sem círculo do "Usar" de um modelo, e o ▶ do bloco de início, na
     arte dos cartões da galeria (out/2026). `criar` é o + DENTRO do círculo. */
  | 'mais'
  | 'play'
  /* A estrela da aba "Modelos prontos" (out/2026): o que já vem escolhido. Cinco
     pontas com as junções redondas da família, para não virar um asterisco. */
  | 'estrela'
  /* A nuvem do armazenamento (#765): a faixa que avisa quando a organização chega
     perto do limite de arquivos, e a que confirma os GB a mais. */
  | 'nuvem';

const DESENHOS: Record<NomeDoIcone, ReactNode> = {
  /* Balão de conversa. A cauda é curta: longa, ela vira uma gota. */
  atendimento: (
    <path d="M17 9.4c0 3.1-3.1 5.6-7 5.6-.85 0-1.66-.12-2.4-.34L4 16l1-2.7C3.76 12.3 3 10.94 3 9.4 3 6.3 6.1 3.8 10 3.8s7 2.5 7 5.6Z" />
  ),

  /* Caixa de entrada: a boca da bandeja fica visível, senão é só um trapézio. */
  fila: (
    <>
      <path d="M3 11.6h3.6l1.2 2h4.4l1.2-2H17" />
      <path d="M3 11.6 5.1 5.2A1.6 1.6 0 0 1 6.6 4.1h6.8a1.6 1.6 0 0 1 1.5 1.1L17 11.6v2.7A1.6 1.6 0 0 1 15.4 16H4.6A1.6 1.6 0 0 1 3 14.3Z" />
    </>
  ),

  funil: <path d="M3.6 4.2h12.8l-4.9 5.9v5.1l-3 1.5v-6.6Z" />,

  clientes: (
    <>
      <circle cx="7.6" cy="7" r="2.7" />
      <path d="M2.9 16.1c.5-2.7 2.4-4.2 4.7-4.2s4.2 1.5 4.7 4.2" />
      <path d="M13.3 4.7a2.7 2.7 0 0 1 0 4.6" />
      <path d="M14.7 12.3c1.4.6 2.3 1.9 2.6 3.8" />
    </>
  ),

  tarefas: (
    <>
      <circle cx="10" cy="10" r="6.9" />
      <path d="m6.9 10.1 2.2 2.2 4-4.6" />
    </>
  ),

  painel: (
    <>
      <path d="M4.2 16.2v-4.4" />
      <path d="M10 16.2V6.9" />
      <path d="M15.8 16.2V3.8" />
    </>
  ),

  /*
   * Pessoa com selo de conferido.
   *
   * O anterior usava um "check" solto ao lado da pessoa, e em 20px ele se
   * confunde com o círculo-com-check de Tarefas. O selo fechado num disco
   * próprio separa os dois de relance.
   */
  identidade: (
    <>
      <circle cx="8.1" cy="6.9" r="2.8" />
      <path d="M3.2 16.2c.4-2.8 2.4-4.4 4.9-4.4.7 0 1.4.13 2 .38" />
      <circle cx="14.2" cy="13.6" r="3.2" />
      <path d="m12.9 13.6 1 1 1.7-2" />
    </>
  ),

  /*
   * CONTROLES, não engrenagem.
   *
   * O anterior era um círculo com oito raios longos, e a leitura imediata é
   * "sol". Uma engrenagem de verdade precisa de dentes, e dentes em 20px viram
   * serrilha. Dois controles deslizantes dizem "ajustar" sem depender de
   * detalhe que não cabe no tamanho.
   */
  /*
   * A engrenagem clássica, que é o que todo mundo procura para "Configurações".
   * Desenhada na grade de 24 e reduzida à de 20; o traço sobe na mesma
   * proporção para ficar com o mesmo peso dos outros ícones.
   */
  configuracoes: (
    <g transform="scale(0.8333)" strokeWidth={2.04}>
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </g>
  ),

  inicio: (
    <path d="m3.6 8.9 6.4-5.3 6.4 5.3v6.1a1.4 1.4 0 0 1-1.4 1.4h-3.3v-4.1H8.3v4.1H5a1.4 1.4 0 0 1-1.4-1.4Z" />
  ),

  /* Painel com a lateral marcada: diz "coluna", não "seta" — e por isso serve
     nos dois sentidos, sem precisar girar quando o menu recolhe. */
  recolher: (
    <>
      <rect x="3" y="4.2" width="14" height="11.6" rx="2" />
      <path d="M8 4.2v11.6" />
    </>
  ),

  sair: (
    <>
      <path d="M12.4 5.4V4.2a1.4 1.4 0 0 0-1.4-1.4H4.6a1.4 1.4 0 0 0-1.4 1.4v11.6a1.4 1.4 0 0 0 1.4 1.4H11a1.4 1.4 0 0 0 1.4-1.4v-1.2" />
      <path d="M8.2 10h8.6" />
      <path d="m14.2 7.4 2.6 2.6-2.6 2.6" />
    </>
  ),

  /* Nota interna: a folha com o canto dobrado, e duas linhas escritas. O
     dobrado é o que a separa de "documento" sem precisar de um lápis por cima,
     que nesta escala vira borrão. */
  nota: (
    <>
      <path d="M11.4 3.2H5.6a1.6 1.6 0 0 0-1.6 1.6v10.4a1.6 1.6 0 0 0 1.6 1.6h8.8a1.6 1.6 0 0 0 1.6-1.6V7.8Z" />
      <path d="M11.4 3.2v3a1.6 1.6 0 0 0 1.6 1.6h3" />
      <path d="M7.2 11.2h5.6" />
      <path d="M7.2 13.8h3.6" />
    </>
  ),

  /* O corpo do sino, o badalo e a alça. A boca é uma linha reta: em 20px, uma
     curva na base vira um borrão e o desenho perde a silhueta de sino. */
  sino: (
    <>
      <path d="M10 3.2a4.6 4.6 0 0 0-4.6 4.6c0 3.1-.9 4.6-1.6 5.4h12.4c-.7-.8-1.6-2.3-1.6-5.4A4.6 4.6 0 0 0 10 3.2Z" />
      <path d="M8.4 15.4a1.8 1.8 0 0 0 3.2 0" />
    </>
  ),

  /* O mesmo sino, riscado: o som está desligado. O risco atravessa a caixa
     inteira para ler como "desligado" também em 16px. */
  'sino-mudo': (
    <>
      <path d="M10 3.2a4.6 4.6 0 0 0-4.6 4.6c0 3.1-.9 4.6-1.6 5.4h12.4c-.7-.8-1.6-2.3-1.6-5.4A4.6 4.6 0 0 0 10 3.2Z" />
      <path d="M8.4 15.4a1.8 1.8 0 0 0 3.2 0" />
      <path d="M3.5 3.5l13 13" />
    </>
  ),

  /* O "@" da menção: o anel de dentro e a volta de fora, que termina aberta
     embaixo, como a letra escrita. */
  arroba: (
    <>
      <circle cx="10" cy="10" r="2.9" />
      <path d="M12.9 10v1.1a2.1 2.1 0 0 0 4.2 0V10a7.1 7.1 0 1 0-2.9 5.7" />
    </>
  ),

  /* A cabeça de seta para a esquerda. Sem haste: numa caixa de 20px a haste
     some no traço e o que sobra é um borrão horizontal. */
  voltar: <path d="M12.5 4.5 7 10l5.5 5.5" />,

  direita: <path d="M7.5 4.5 13 10l-5.5 5.5" />,

  lixeira: (
    <>
      <path d="M3.5 5.5h13" />
      <path d="M8 5.5V4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1.5" />
      <path d="M5 5.5 5.8 16a1 1 0 0 0 1 .9h6.4a1 1 0 0 0 1-.9L15 5.5" />
      <path d="M8.5 9v4.5M11.5 9v4.5" />
    </>
  ),

  /* A seta que SOBE da bandeja: o gesto oposto ao do portal, que entra. */
  enviar: (
    <>
      <path d="M10 12.8V4.2" />
      <path d="m6.9 7.1 3.1-3.1 3.1 3.1" />
      <path d="M3.6 12.8v2.4a1.6 1.6 0 0 0 1.6 1.6h9.6a1.6 1.6 0 0 0 1.6-1.6v-2.4" />
    </>
  ),

  /* Olho: abrir para ver, sem mudar nada. */
  olho: (
    <>
      <path d="M2.2 10S5 4.8 10 4.8 17.8 10 17.8 10 15 15.2 10 15.2 2.2 10 2.2 10Z" />
      <circle cx="10" cy="10" r="2.4" />
    </>
  ),

  /* Folha com a dobra e linhas de texto: o documento que se lê. */
  documento: (
    <>
      <path d="M11.5 2.8H6a1.4 1.4 0 0 0-1.4 1.4v11.6A1.4 1.4 0 0 0 6 17.2h8a1.4 1.4 0 0 0 1.4-1.4V6.7Z" />
      <path d="M11.5 2.8v3.9h3.9" />
      <path d="M7.4 10.4h5.2M7.4 13.2h5.2" />
    </>
  ),

  /* Folha com a dobra e SEM texto: o arquivo que não se sabe o que é. */
  arquivo: (
    <>
      <path d="M11.5 2.8H6a1.4 1.4 0 0 0-1.4 1.4v11.6A1.4 1.4 0 0 0 6 17.2h8a1.4 1.4 0 0 0 1.4-1.4V6.7Z" />
      <path d="M11.5 2.8v3.9h3.9" />
    </>
  ),

  /*
   * A estrela grande de quatro pontas e uma pequena: o sinal que o mercado já
   * ensinou como "IA".
   *
   * O único PREENCHIDO da família, e de propósito: em traço, as pontas finas
   * fecham num borrão em 20px e a estrela vira um losango. Desenhada na grade
   * de 24 da especificação e reduzida para a caixa de 20, em vez de redesenhada
   * à mão com arredondamentos que a afastariam do original.
   */
  ia: (
    <g fill="currentColor" stroke="none" transform="scale(0.8333)">
      <path d="M12 2l2.2 6.3L20.5 10.5l-6.3 2.2L12 19l-2.2-6.3L3.5 10.5l6.3-2.2z" />
      <path d="M19 15l.9 2.1L22 18l-2.1.9L19 21l-.9-2.1L16 18l2.1-.9z" />
    </g>
  ),

  /* A lupa limpa: buscar. Mesmo aro e mesmo cabo da `lupa`, sem o "+". */
  buscar: (
    <>
      <circle cx="8.8" cy="8.8" r="5.4" />
      <path d="m12.8 12.8 3.8 3.8" />
    </>
  ),

  /* O ×: tirar o que está aplicado. As hastes param antes da borda para ele
     pesar o mesmo que os outros num chip de 24px. */
  fechar: <path d="m5.8 5.8 8.4 8.4M14.2 5.8l-8.4 8.4" />,

  /* O fone, de traço. */
  telefone: (
    <path d="M4.2 4.6a1.6 1.6 0 0 1 1.6-1.4h1.6a1 1 0 0 1 1 .8l.6 2.6a1 1 0 0 1-.4 1l-1.3 1a9.6 9.6 0 0 0 4.1 4.1l1-1.3a1 1 0 0 1 1-.4l2.6.6a1 1 0 0 1 .8 1v1.6a1.6 1.6 0 0 1-1.4 1.6C9.2 17 3 10.8 4.2 4.6Z" />
  ),

  /* O envelope. */
  email: (
    <>
      <rect x="2.8" y="4.6" width="14.4" height="10.8" rx="2" />
      <path d="m3.4 6 6.6 5 6.6-5" />
    </>
  ),

  /* A etiqueta de preço, com o furo. */
  etiqueta: (
    <>
      <path d="M3.2 3.2h6.2l7.4 7.4a1.4 1.4 0 0 1 0 2l-4.2 4.2a1.4 1.4 0 0 1-2 0L3.2 9.4Z" />
      <circle cx="7" cy="7" r="1.1" />
    </>
  ),

  /* Um prédio com janelas: as organizações que a Cliqfy atende. */
  predio: (
    <>
      <path d="M4.6 16.8V4.6a1.4 1.4 0 0 1 1.4-1.4h5a1.4 1.4 0 0 1 1.4 1.4v12.2" />
      <path d="M12.4 8.2h2.6a1.4 1.4 0 0 1 1.4 1.4v7.2" />
      <path d="M3 16.8h14M7.4 6.4h2.2M7.4 9.4h2.2M7.4 12.4h2.2" />
    </>
  ),

  /* A caixa de entrada: a bandeja com a boca. */
  caixa: (
    <>
      <path d="M3.2 11.4h3.6l1.2 2.2h4l1.2-2.2h3.6" />
      <path d="M3.2 11.4V7a2 2 0 0 1 2-2h9.6a2 2 0 0 1 2 2v4.4v3a2 2 0 0 1-2 2H5.2a2 2 0 0 1-2-2Z" />
    </>
  ),

  /* Duas folhas: copiar. */
  copiar: (
    <>
      <rect x="7" y="7" width="9.6" height="9.6" rx="1.6" />
      <path d="M13 7V5a1.6 1.6 0 0 0-1.6-1.6H5A1.6 1.6 0 0 0 3.4 5v6.4A1.6 1.6 0 0 0 5 13h2" />
    </>
  ),

  /* Lupa com o "+": examinar de perto, e não buscar. */
  lupa: (
    <>
      <circle cx="8.8" cy="8.8" r="5.4" />
      <path d="m12.8 12.8 3.8 3.8" />
      <path d="M8.8 6.6v4.4M6.6 8.8h4.4" />
    </>
  ),

  /* Lápis inteiro, sozinho. Por cima de uma folha ele vira borrão (ver `nota`);
     solto, tem a caixa toda para a ponta e a virola. */
  lapis: (
    <>
      <path d="M13.6 3.6a1.9 1.9 0 0 1 2.7 2.7L7.2 15.4l-3.6.9.9-3.6Z" />
      <path d="m12.2 5 2.8 2.8" />
    </>
  ),

  /* A seta COM haste: "o que vem depois". O chevron de `direita` diz "abre", e
     os dois gestos não podem ter o mesmo desenho. A haste se sustenta em 20px,
     como a do `sair`. */
  seta: (
    <>
      <path d="M3.8 10h12.4" />
      <path d="m11.6 5.4 4.6 4.6-4.6 4.6" />
    </>
  ),

  /* O raio: dois triângulos desencontrados. Pontas redondas, como o resto da
     família — em ponta viva ele pesaria mais que os vizinhos do menu. */
  automacao: <path d="M11.2 2.6 4.6 11.1h4.8l-.6 6.3 6.6-8.5h-4.8Z" />,

  /* Busto: cabeça e ombros. Sem o traço do pescoço, que em 12px vira ruído. */
  pessoa: (
    <>
      <circle cx="10" cy="6.7" r="3.3" />
      <path d="M3.3 17.5c0-3.3 3.3-5 6.7-5s6.7 1.7 6.7 5" />
    </>
  ),

  /* Relógio: os ponteiros saem do centro, e o das horas é o mais curto. */
  relogio: (
    <>
      <circle cx="10" cy="10" r="7.5" />
      <path d="M10 5.8V10l2.5 1.7" />
    </>
  ),

  /* Calendário com o "+" dentro: marcar uma tarefa para depois. */
  'tarefa-nova': (
    <>
      <rect x="2.5" y="3.3" width="15" height="14.2" rx="2.5" />
      <path d="M6.7 1.7v3.3M13.3 1.7v3.3M10 8.3v5M7.5 10.8h5" />
    </>
  ),

  /* A pessoa e a seta saindo dela: o card vai para outra mão. */
  passar: (
    <>
      <circle cx="7.5" cy="6.7" r="3.3" />
      <path d="M1.7 17.5c0-3.3 2.5-5 5.8-5 1.25 0 2.3.25 3.25.67" />
      <path d="M13.3 12.5h5M15.8 10l2.5 2.5-2.5 2.5" />
    </>
  ),

  /* O tema da tela: o sol é o claro, a lua é o escuro. */
  sol: (
    <>
      <circle cx="10" cy="10" r="3.4" />
      <path d="M10 2.6v1.6M10 15.8v1.6M2.6 10h1.6M15.8 10h1.6M4.8 4.8l1.1 1.1M14.1 14.1l1.1 1.1M4.8 15.2l1.1-1.1M14.1 5.9l1.1-1.1" />
    </>
  ),
  lua: <path d="M15.8 12.4A6.6 6.6 0 0 1 7.6 4.2a6.6 6.6 0 1 0 8.2 8.2Z" />,

  /* O chevron que desce: "abre uma lista". */
  abaixo: <path d="m5 7.5 5 5 5-5" />,

  /* O ✓: a opção que está escolhida. */
  marcado: <path d="m4.5 10.5 3.6 3.5 7.4-8" />,

  /* A bandeira no mastro: a prioridade. */
  prioridade: <path d="M5.5 17V3.5M5.5 4.2h8.6l-1.8 3.4 1.8 3.4H5.5" />,

  /* Balão redondo com o telefone dentro. */
  whatsapp: (
    <>
      <path d="M10 3a7 7 0 0 0-6.1 10.4L3 17l3.7-.9A7 7 0 1 0 10 3Z" />
      <path d="M7.6 7.4c0 2.8 2.2 5 5 5l.9-1.2-1.6-.8-.7.7a3.4 3.4 0 0 1-2.3-2.3l.7-.7-.8-1.6Z" />
    </>
  ),

  /* A câmera de cantos redondos: quadrado, lente e o ponto do flash. */
  instagram: (
    <>
      <rect x="3" y="3" width="14" height="14" rx="4" />
      <circle cx="10" cy="10" r="3.2" />
      <circle cx="14.2" cy="5.8" r="0.6" />
    </>
  ),

  /* O "f" dentro do círculo. */
  facebook: (
    <>
      <circle cx="10" cy="10" r="7.5" />
      <path d="M12.9 5.6h-1.3c-1.2 0-1.9.7-1.9 1.9v9.9M8 10.6h4.6" />
    </>
  ),

  /* A janela do navegador com as linhas de uma conversa. */
  'web-chat': (
    <>
      <rect x="2.5" y="4" width="15" height="12" rx="2" />
      <path d="M2.5 7.5h15M6.5 11h7M6.5 13.3h4" />
    </>
  ),

  /* Os sinais de código: o canal que outro sistema alimenta. */
  api: <path d="m6.5 6-4 4 4 4M13.5 6l4 4-4 4M11.2 4.8 8.8 15.2" />,

  /* A nota musical com a curva do topo. */
  tiktok: (
    <>
      <path d="M11.5 3v9.2a3 3 0 1 1-3-3" />
      <path d="M11.5 3c.3 2 1.7 3.5 3.8 3.7" />
    </>
  ),

  /* Evento de portal: o que chega DE FORA para dentro da bandeja. A seta
     aponta para baixo porque é entrada, e é o mesmo gesto do ícone da fila. */
  portal: (
    <>
      <path d="M10 3.2v7.6" />
      <path d="m6.9 7.9 3.1 3.1 3.1-3.1" />
      <path d="M3.6 12.8v2.4a1.6 1.6 0 0 0 1.6 1.6h9.6a1.6 1.6 0 0 0 1.6-1.6v-2.4" />
    </>
  ),

  criar: (
    <>
      <circle cx="10" cy="10" r="7" />
      <path d="M10 6.6v6.8M6.6 10h6.8" />
    </>
  ),

  trocar: (
    <>
      <path d="M3.8 7h12.4" />
      <path d="m13.2 4 3 3-3 3" />
      <path d="M16.2 13H3.8" />
      <path d="m6.8 10-3 3 3 3" />
    </>
  ),

  /*
   * A folha com a grade de células: o que se reconhece como "abre no Excel"
   * sem copiar a marca de ninguém. A coluna da esquerda mais estreita é a dos
   * rótulos, como numa planilha de verdade.
   */
  planilha: (
    <>
      <rect x="3.4" y="3.4" width="13.2" height="13.2" rx="1.6" />
      <path d="M3.4 7.8h13.2M3.4 12.2h13.2M7.8 3.4v13.2" />
    </>
  ),

  check: <path d="m4.6 10.4 3.4 3.4 7.4-7.6" />,

  alerta: (
    <>
      <path d="M10 3.3 2.6 16.1h14.8Z" />
      <path d="M10 8.2v3.6" />
      <path d="M10 14.1h.01" />
    </>
  ),

  pausa: <path d="M7.2 4.6v10.8M12.8 4.6v10.8" />,

  /* Duas setas em volta: o que roda de novo, a cada vez que acontece. */
  repetir: (
    <>
      <path d="M4 9.2V8a3 3 0 0 1 3-3h9" />
      <path d="m13.6 2.6 2.4 2.4-2.4 2.4" />
      <path d="M16 10.8V12a3 3 0 0 1-3 3H4" />
      <path d="m6.4 17.4-2.4-2.4 2.4-2.4" />
    </>
  ),

  /* O frasco de laboratório: em teste, ainda não está valendo para todos. */
  frasco: (
    <>
      <path d="M7.6 2.8h4.8M8.4 2.8v4.6L4 15.2a1.4 1.4 0 0 0 1.2 2h9.6a1.4 1.4 0 0 0 1.2-2L11.6 7.4V2.8" />
      <path d="M6 12.2h8" />
    </>
  ),

  /* O cartão com as opções numeradas: o menu de atendimento. */
  menu: (
    <>
      <rect x="3" y="3" width="14" height="14" rx="2.4" />
      <path d="M6.6 7.2h.01M9.4 7.2h4M6.6 10h.01M9.4 10h4M6.6 12.8h.01M9.4 12.8h4" />
    </>
  ),

  calendario: (
    <>
      <rect x="2.8" y="4" width="14.4" height="13" rx="2.2" />
      <path d="M2.8 8.2h14.4M6.6 2.4V5.4M13.4 2.4V5.4" />
    </>
  ),

  mais: <path d="M10 4.4v11.2M4.4 10h11.2" />,

  play: <path d="M7 4.9v10.2c0 .6.7 1 1.2.7l7.6-5.1c.5-.3.5-1 0-1.4L8.2 4.2c-.5-.3-1.2 0-1.2.7Z" />,

  estrela: <path d="m10 2.8 2.2 4.6 5 .7-3.6 3.5.9 5-4.5-2.4-4.5 2.4.9-5-3.6-3.5 5-.7Z" />,

  nuvem: <path d="M6 15.5h8.5a3 3 0 0 0 .4-6 4.5 4.5 0 0 0-8.7-1A3.5 3.5 0 0 0 6 15.5Z" />,
};

export type IconeProps = {
  nome: NomeDoIcone;
  /** Só quando o ícone é a única coisa que identifica o controle. */
  rotulo?: string;
};

export function Icone({ nome, rotulo }: IconeProps) {
  return (
    <svg
      width={20}
      height={20}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={rotulo ? 'img' : undefined}
      aria-label={rotulo}
      aria-hidden={rotulo ? undefined : true}
    >
      {DESENHOS[nome]}
    </svg>
  );
}
